import type {
  AgentCapabilities,
  AgentChatCapabilities,
  AgentMode,
} from '@tabbio/types';
import arCommon from '../i18n/locales/ar/common';
import enCommon from '../i18n/locales/en/common';
import {
  applyAgentCapabilityChange,
  readAgentCapabilitiesPreference,
  readKnownAgentCapabilities,
  resolveAgentCapabilitiesRequest,
  type AgentCapabilitiesPreference,
} from './agent-capabilities-preference';

/**
 * The composer's inline tokens.
 *
 * `@` opens the sources menu at the caret and `/` opens the commands menu
 * (Tabbio skills). Picking a source inserts a mention such as `@Web search`,
 * which is a per-message capability: it sets that one turn's Research, Image
 * or Result on top of the sticky preference and is removed from the text
 * before the message is sent. The server contract does not change; the
 * mention only changes the `capabilities` field the message already carries.
 */

export type ComposerToken =
  | { kind: 'mention'; query: string; start: number }
  | { kind: 'command'; query: string; start: 0 };

const MENTION_TOKEN = /(^|\s)@([^\s@]*)$/;
// Slash commands stay start-of-draft only, the rule the skills menu has
// always used: a `/` later in a sentence is just a slash.
const COMMAND_TOKEN = /^\/([^\s]*)$/;

/**
 * The token being typed at the end of the draft, if any. `mention` is an `@`
 * that starts the draft or follows whitespace; `command` is a draft that is a
 * single `/word`. The query is lower-cased for matching.
 */
export function parseComposerToken(
  draft: string,
  options: { mentions?: boolean; commands?: boolean } = {},
): ComposerToken | null {
  const { mentions = true, commands = true } = options;
  if (commands) {
    const command = COMMAND_TOKEN.exec(draft);
    if (command) {
      return { kind: 'command', query: command[1]!.toLowerCase(), start: 0 };
    }
  }
  if (mentions) {
    const mention = MENTION_TOKEN.exec(draft);
    if (mention) {
      return {
        kind: 'mention',
        query: mention[2]!.toLowerCase(),
        start: mention.index + mention[1]!.length,
      };
    }
  }
  return null;
}

/** The draft with the token being typed replaced by `insert`. */
export function replaceComposerToken(
  draft: string,
  token: ComposerToken | null,
  insert: string,
): string {
  const head = token ? draft.slice(0, token.start) : draft;
  if (!insert) return head;
  // Picking from the "+" menu (no token) appends to whatever is typed.
  const separator = !token && head.length > 0 && !/\s$/.test(head) ? ' ' : '';
  return `${head}${separator}${insert}`;
}

/**
 * Keyboard movement through a menu: down and up wrap around both ends. With
 * nothing to move through the index stays at 0.
 */
export function moveComposerMenuIndex(
  current: number,
  direction: 1 | -1,
  length: number,
): number {
  if (length <= 0) return 0;
  return (((current + direction) % length) + length) % length;
}

// ── Mentions ───────────────────────────────────────────────────────────────

export type CapabilityMentionKey =
  | 'research'
  | 'image'
  | 'document'
  | 'slides'
  | 'page';

export const CAPABILITY_MENTION_KEYS: readonly CapabilityMentionKey[] = [
  'research',
  'image',
  'document',
  'slides',
  'page',
];

export type CapabilityMentionLabels = Record<
  CapabilityMentionKey,
  readonly string[]
>;

/**
 * Every label a mention can be written with, English and Arabic alike: a
 * draft typed before switching language still means the same thing.
 */
export const CAPABILITY_MENTION_LABELS: CapabilityMentionLabels = {
  research: [
    enCommon.chat.composer.mentions.research,
    arCommon.chat.composer.mentions.research,
  ],
  image: [
    enCommon.chat.composer.mentions.image,
    arCommon.chat.composer.mentions.image,
  ],
  document: [
    enCommon.chat.composer.mentions.document,
    arCommon.chat.composer.mentions.document,
  ],
  slides: [
    enCommon.chat.composer.mentions.slides,
    arCommon.chat.composer.mentions.slides,
  ],
  page: [
    enCommon.chat.composer.mentions.page,
    arCommon.chat.composer.mentions.page,
  ],
};

export type CapabilityMention = {
  key: CapabilityMentionKey;
  /** Index of the `@`. */
  start: number;
  /** Index just past the label. */
  end: number;
};

// What may follow a label: the end, whitespace, or sentence punctuation
// (Latin and Arabic), so `@Slides, please` still counts.
const LABEL_END = /^(?:$|[\s.,;:!?،؛؟])/;

function isAvailable(
  key: CapabilityMentionKey,
  capabilities: AgentCapabilities | null | undefined,
): boolean {
  // Before availability is known the mention goes out as picked; the server
  // resolves availability again either way.
  if (!capabilities) return true;
  if (key === 'research') return capabilities.research.available;
  if (key === 'image') {
    return capabilities.image.available && capabilities.artifacts.available;
  }
  return capabilities.artifacts.available;
}

/**
 * The mentions in a message, in the order they appear. A token counts only
 * when it is one of the labels (any case), starts the text or follows
 * whitespace, and ends at a word boundary; a capability this account cannot
 * use is not a mention. Anything else, including a label edited by hand, is
 * plain text.
 */
export function findCapabilityMentions(
  text: string,
  options: {
    labels?: CapabilityMentionLabels;
    capabilities?: AgentCapabilities | null;
  } = {},
): CapabilityMention[] {
  const labels = options.labels ?? CAPABILITY_MENTION_LABELS;
  // Longest first, so a label that is the prefix of another never wins.
  const candidates = CAPABILITY_MENTION_KEYS.flatMap((key) =>
    labels[key].map((label) => ({ key, label: label.toLocaleLowerCase() })),
  ).sort((a, b) => b.label.length - a.label.length);
  const lower = text.toLocaleLowerCase();
  const mentions: CapabilityMention[] = [];

  for (let index = text.indexOf('@'); index !== -1; index = text.indexOf('@', index + 1)) {
    if (index > 0 && !/\s/.test(text[index - 1]!)) continue;
    const rest = lower.slice(index + 1);
    const match = candidates.find(
      ({ label }) =>
        label.length > 0 &&
        rest.startsWith(label) &&
        LABEL_END.test(rest.slice(label.length)),
    );
    if (!match || !isAvailable(match.key, options.capabilities)) continue;
    mentions.push({ key: match.key, start: index, end: index + 1 + match.label.length });
  }
  return mentions;
}

/** The message without its mentions, tidied so no double space is left. */
export function stripCapabilityMentions(
  text: string,
  mentions: readonly CapabilityMention[],
): string {
  let result = text;
  for (const mention of [...mentions].sort((a, b) => b.start - a.start)) {
    const before = result.slice(0, mention.start);
    let after = result.slice(mention.end);
    // Take one following space with the token, or the preceding one when the
    // token ends the line, so the words around it close up.
    if (/^[ \t]/.test(after)) after = after.slice(1);
    const trimmedBefore =
      /[ \t]$/.test(before) && (after === '' || after.startsWith('\n'))
        ? before.slice(0, -1)
        : before;
    result = `${trimmedBefore}${after}`;
  }
  return result.trim();
}

/**
 * The preference for one message: each mention applied in order, through the
 * same coupling the controls use. Web search turns Research on; Image,
 * Document, Slides and Page set the Result, and the last one wins.
 */
export function applyCapabilityMentions(
  preference: AgentCapabilitiesPreference,
  keys: readonly CapabilityMentionKey[],
): AgentCapabilitiesPreference {
  return keys.reduce<AgentCapabilitiesPreference>((current, key) => {
    if (key === 'research') {
      return applyAgentCapabilityChange(current, 'research', 'on');
    }
    return applyAgentCapabilityChange(current, 'artifact', key);
  }, preference);
}

const SKILL_COMMAND = /^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\s|$)/i;

export type MessageCapabilities = {
  text: string;
  capabilities: Required<AgentChatCapabilities>;
};

/**
 * One message's text and capabilities once its mentions are read. Returns
 * null when the message carries no mention (the caller sends it as typed with
 * the sticky controls), is a slash command (the server ignores the controls
 * there), or would be empty without its mentions (a mention needs a message
 * to ride on, so the text goes out as typed). `allowEmpty` is for a message
 * that carries attachments: they are the message, so its mentions still
 * apply and it goes out with no text, as an attachment-only message does.
 */
export function resolveMessageCapabilities(params: {
  text: string;
  preference: AgentCapabilitiesPreference;
  capabilities: AgentCapabilities | null | undefined;
  labels?: CapabilityMentionLabels;
  allowEmpty?: boolean;
}): MessageCapabilities | null {
  const { text, preference, capabilities } = params;
  if (SKILL_COMMAND.test(text.trim())) return null;
  const mentions = findCapabilityMentions(text, {
    labels: params.labels,
    capabilities,
  });
  if (mentions.length === 0) return null;
  const stripped = stripCapabilityMentions(text, mentions);
  if (!stripped && !params.allowEmpty) return null;
  return {
    text: stripped,
    capabilities: resolveAgentCapabilitiesRequest(
      applyCapabilityMentions(
        preference,
        mentions.map((mention) => mention.key),
      ),
      capabilities,
    ),
  };
}

/**
 * Read at send time by the chat runtimes, like the sticky controls. Employer
 * threads have no capability controls, so an `@` there is plain text. The
 * phone passes Auto as `preference`: it has no sticky controls, so a message
 * runs with Auto plus its own mentions and chips.
 */
export function readAgentMessageCapabilities(
  mode: AgentMode,
  text: string,
  options: { preference?: AgentCapabilitiesPreference; allowEmpty?: boolean } = {},
): MessageCapabilities | null {
  if (mode !== 'seeker') return null;
  return resolveMessageCapabilities({
    text,
    preference: options.preference ?? readAgentCapabilitiesPreference(),
    capabilities: readKnownAgentCapabilities(),
    allowEmpty: options.allowEmpty,
  });
}

/**
 * The text a user message shows in the thread: exactly the text the chat
 * runtime sends (`readAgentMessageCapabilities`), which is also what the
 * server stores and returns on reload. The bubble never shows a mention the
 * server will not have. `allowEmpty` matches the runtime for a message with
 * attachments.
 */
export function agentMessageDisplayText(
  mode: AgentMode,
  text: string,
  options: { allowEmpty?: boolean } = {},
): string {
  return readAgentMessageCapabilities(mode, text, options)?.text ?? text;
}

// ── The phone's chips ──────────────────────────────────────────────────────

/**
 * The phone's "+" sheet sets the next message's capabilities with chips above
 * the input. A chip is a mention the user did not type: Web search sits beside
 * at most one result (Image, Document, Slides or Page), and a new result
 * replaces the old one, the rule typed mentions follow (the last one wins).
 * Chips keep one order, Web search first.
 */
export function addCapabilityChip(
  chips: readonly CapabilityMentionKey[],
  key: CapabilityMentionKey,
): CapabilityMentionKey[] {
  const kept: readonly CapabilityMentionKey[] =
    key === 'research'
      ? chips.filter((chip) => chip !== 'research')
      : chips.filter((chip) => chip === 'research');
  return CAPABILITY_MENTION_KEYS.filter((chip) => chip === key || kept.includes(chip));
}

export function removeCapabilityChip(
  chips: readonly CapabilityMentionKey[],
  key: CapabilityMentionKey,
): CapabilityMentionKey[] {
  return chips.filter((chip) => chip !== key);
}

/**
 * The draft as it is sent with its chips: each chip written in front as the
 * mention it stands for, so the chat runtime reads it at send time exactly as
 * it reads a typed "@Document", and removes it before the message leaves the
 * phone. The bubble strips it the same way (`agentMessageDisplayText`).
 */
export function withCapabilityChips(
  text: string,
  chips: readonly CapabilityMentionKey[],
  labelFor: (key: CapabilityMentionKey) => string,
): string {
  if (chips.length === 0) return text;
  const mentions = chips.map((key) => `@${labelFor(key)}`).join(' ');
  return text.length > 0 ? `${mentions} ${text}` : mentions;
}
